import React, { useEffect, useState } from "react";
import { Form, Input, Button, Card, Typography, Select, Row, Col, Checkbox, Modal } from "antd";
import { User, Mail, Phone, IdCard, LockKeyhole, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { loginSuccess, signup } from "../store/slices/authSlice";
import useLocation from "../hooks/useLocation";
import axios from 'axios';
import logo from "../assets/images/XynexAI_Logo.png";
import UserAgreementContent from "./UserAgreementContent";
import PrivacyPolicyContent from "./PrivacyPolicyContent";

const { Title, Text } = Typography;
const { Option } = Select;

const SignUp = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.auth);
  const [userAgreementModalVisible, setUserAgreementModalVisible] = useState(false);
  const [termsModalVisible, setTermsModalVisible] = useState(false);
  const [hasOpenedAgreement, setHasOpenedAgreement] = useState(false);

  const {
    countries,
    states,
    cities,
    selectedCountry,
    selectedState,
    selectedCity,
    setSelectedCountry,
    setSelectedState,
    setSelectedCity,
    loading: locationLoading,
  } = useLocation();

  // keep form.setFieldsValue effect so form picks up selection changes
  useEffect(() => {
    form.setFieldsValue({
      country: selectedCountry || undefined,
      state: selectedState || undefined,
      city: selectedCity || undefined,
    });
  }, [selectedCountry, selectedState, selectedCity, form]);

  useEffect(() => {
  if (selectedCountry) {
    const country = countries.find(c => c.name === selectedCountry);
    if (country?.phonecode) {
      form.setFieldsValue({ countryCode: `+${country.phonecode}` });
    }
  }
}, [selectedCountry, countries]);


  const onFinish = async (values) => {
    try {
      const fullPhone = `${values.countryCode}${values.phone}`;

      const payload = {
        ...values,
        phone: fullPhone,
      };

      delete payload.countryCode;

      const result = await dispatch(signup(payload)).unwrap();

      if (result && (result.status === true || result.status === "success")) {
        toast.success(
          "Successfully Registered! Check your email for the verification code.",{theme: "colored"}
        );
        navigate("/verify-email", {
          replace: true,
          state: { email: values.email, phone: values.phone, fromSignup: true },
        });
      }
    } catch (error) {
      console.error("Signup failed:", error);
      toast.error(error.message || "Signup failed. Please try again.",{theme: "colored"});
    }
  };

  const onFinishFailed = (errorInfo) => {
    // console.log("Failed:", errorInfo);
    toast.error(`Please check the form for ${errorInfo} and try again.`,{theme: "colored"});
  };

  const handleUserAgreementAccept = () => {
    // mark as enabled (defensive)
    setHasOpenedAgreement(true);

    // set accepted
    form.setFieldsValue({ terms: true });
    // clear validation errors immediately
    form.validateFields(["terms"]).catch(() => {});
    setUserAgreementModalVisible(false);
  };

  const handleTermsAccept = () => {
    setHasOpenedAgreement(true);
    form.setFieldsValue({ terms: true });
    form.validateFields(["terms"]).catch(() => {});
    setTermsModalVisible(false);
  };

  const prefixSelector = (
  <Form.Item name="countryCode" noStyle initialValue="+91">
    <Select
      showSearch
      style={{ width: 80 }}
      optionFilterProp="children"
      filterOption={(input, option) =>
        String(option?.children)
          .toLowerCase()
          .includes(input.toLowerCase())
      }
    >
      {countries.map((c) => (
        <Option key={c.iso2} value={`+${c.phonecode}`}>
          +{c.phonecode}
        </Option>
      ))}
    </Select>
  </Form.Item>
);


  return (
    <div className="min-h-screen flex flex-col items-center justify-start overflow-y-auto" style={{ WebkitOverflowScrolling: "touch" }}>
      <header className="flex justify-center items-center flex-shrink-0">
        <img
          src={logo}
          alt="XynexAi Logo"
          className="w-24 sm:w-28 h-auto max-h-28 object-contain"
        />
      </header>

      <div className="w-full max-w-[clamp(22rem,95vw,42rem)] px-4 mb-8">
        <Card className="shadow-2xl rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm">
          <div className="text-center p-4 sm:p-6">
            <Text className="text-white text-xl sm:text-xl font-sans">
              Sign up to access your dashboard
            </Text>
          </div>

          <Form
            form={form}
            name="SignUp"
            initialValues={{ terms: false }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            layout="vertical"
          >
            <Row gutter={[12]}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      First Name
                    </span>
                  }
                  name="firstName"
                  rules={[
                    { required: true, message: "Please input your name!" },
                    {
                      min: 2,
                      message: "Name must be at least 2 characters long!",
                    },
                  ]}
                >
                  <Input
                    prefix={<User className="text-gray-500" />}
                    placeholder="John"
                    size="large"
                    className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      Last Name
                    </span>
                  }
                  name="lastName"
                  rules={[
                    { required: true, message: "Please input your name!" },
                  ]}
                >
                  <Input
                    prefix={<User className="text-gray-500" />}
                    placeholder="Doe"
                    size="large"
                    className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="country"
                      label={
                        <span className="text-gray-200 font-medium text-base">
                          Country
                        </span>
                      }
                      rules={[
                        {
                          required: true,
                          message: "Please select your country!",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select your country"
                        size="large"
                        value={selectedCountry || undefined}
                        onChange={(val) => setSelectedCountry(val)}
                        loading={locationLoading}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.children ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        className="h-10 bg-gray-50 border-gray-200 rounded-lg"
                        allowClear
                      >
                        {countries.map((c) => (
                          <Option key={c.name} value={c.name}>
                            {c.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="state"
                      label={
                        <span className="text-gray-200 font-medium text-base">
                          State
                        </span>
                      }
                      rules={[
                        { required: true, message: "Please enter your state!" },
                      ]}
                    >
                      <Select
                        placeholder="Select state"
                        size="large"
                        value={selectedState || undefined}
                        onChange={(val) => setSelectedState(val)}
                        disabled={!selectedCountry}
                        loading={locationLoading}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.children ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        className="h-10 bg-gray-50 border-gray-200 rounded-lg"
                        allowClear
                      >
                        {states.map((s) => (
                          <Option key={s.iso2 || s} value={s.name || s}>
                            {s.name || s}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="city"
                      label={
                        <span className="text-gray-200 font-medium text-base">
                          City
                        </span>
                      }
                      rules={[
                        { required: true, message: "Please enter your city!" },
                      ]}
                    >
                      <Select
                        placeholder="Select city"
                        size="large"
                        value={selectedCity || undefined}
                        onChange={(val) => setSelectedCity(val)}
                        disabled={!selectedState}
                        loading={locationLoading}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.children ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        className="h-10 bg-gray-50 border-gray-200 rounded-lg"
                        allowClear
                      >
                        {cities.map((ct, idx) => (
                          <Option
                            key={ct.id || `${ct.name || ct}-${idx}`}
                            value={ct.name || ct}
                          >
                            {ct.name || ct}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </Col>

              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  className="mt-1"
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      Email
                    </span>
                  }
                  name="email"
                  rules={[
                    {
                      type: "email",
                      message: "Please enter a valid email address!",
                    },
                    { required: true, message: "Please input your email!" },
                  ]}
                >
                  <Input
                    prefix={<Mail className="text-gray-500" />}
                    placeholder="john.doe@company.com"
                    size="large"
                    className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      Phone
                    </span>
                  }
                  name="phone"
                  rules={[
                    {
                      required: true,
                      message: "Please input your phone number!",
                    },
                    { pattern: /^[0-9]{6,15}$/, message: "Enter valid phone number!" },
                  ]}
                >
                  <Input
                    addonBefore={prefixSelector}
                    onInput={(e) => (e.target.value = e.target.value.replace(/\D/g, ""))}
                    prefix={<Phone className="text-gray-500" />}
                    placeholder="9870000000"
                    size="large"
                    className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
                    maxLength={10}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="domain"
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      Domain
                    </span>
                  }
                  rules={[{ required: true, message: "Please select domain!" }]}
                >
                  <Select
                    showSearch
                    placeholder="Select your domain"
                    size="large"
                    className="h-10 bg-gray-50 border-gray-200 rounded-lg"
                  >
                    <Option value="finance">Finance</Option>
                    <Option value="it">IT</Option>
                    <Option value="audit head">Audit Head</Option>
                    <Option value="cxo">CXO</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      Organization
                    </span>
                  }
                  name="organization"
                  rules={[
                    {
                      required: true,
                      message: "Please input your organization!",
                    },
                    {
                      min: 2,
                      message:
                        "Organization must be at least 2 characters long!",
                    },
                  ]}
                >
                  <Input
                    prefix={<IdCard className="text-gray-500" />}
                    placeholder="Enter your organization name"
                    size="large"
                    className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="password"
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      Password
                    </span>
                  }
                  rules={[
                    { required: true, message: "Please input your password!" },
                    {
                      min: 8,
                      max: 15,
                      message:
                        "Password must be between 8 and 15 characters long!",
                    },
                    {
                      pattern:
                        /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*]).+$/,
                      message:
                        "Password must contain at least 1 uppercase letters, 1 special characters, 1 numbers, and 1 lowercase letters!",
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<LockKeyhole className="text-gray-500" />}
                    placeholder="Password"
                    size="large"
                    className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="confirm"
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      Confirm Password
                    </span>
                  }
                  dependencies={["password"]}
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      message: "Please confirm your password!",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error(
                            "The two passwords that you entered do not match!"
                          )
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockKeyhole className="text-gray-500" />}
                    placeholder="Confirm Password"
                    size="large"
                    className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
                  />
                </Form.Item>
              </Col>

              {/* Terms and Conditions Checkbox */}
              <Col xs={24}>
                <Form.Item
                  name="terms"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(
                                "Please accept the terms and conditions"
                              )
                            ),
                    },
                  ]}
                >
                  <Checkbox
                    disabled={!hasOpenedAgreement}
                    checked={!!form.getFieldValue("terms")}
                    onChange={(e) => {
                      const alreadyAccepted = !!form.getFieldValue("terms");

                      // If already accepted, allow unchecking:
                      if (alreadyAccepted) {
                        form.setFieldsValue({ terms: e.target.checked });
                        form.validateFields(["terms"]).catch(() => {});
                        return;
                      }

                      // Not accepted yet — open the User Agreement modal (or Terms modal if you prefer)
                      // This ensures the user cannot simply tick the checkbox without accepting.
                      setUserAgreementModalVisible(true);
                      // ensure checkbox is enabled (it should already be if they clicked a link)
                      setHasOpenedAgreement(true);
                    }}
                    className={
                      !hasOpenedAgreement
                        ? "checkbox-disabled"
                        : "text-gray-200"
                    }
                  >
                    I have read and agree to the{" "}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setHasOpenedAgreement(true); // enable the checkbox
                        setUserAgreementModalVisible(true); // open that modal
                      }}
                      className="text-[#cca1e4] hover:text-[#bd9bd1] underline bg-transparent border-none cursor-pointer p-0"
                    >
                      User License Acceptance Agreement
                    </button>{" "}
                    and the{" "}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setHasOpenedAgreement(true); // enable the checkbox
                        setTermsModalVisible(true); // open Terms modal
                      }}
                      className="text-[#cca1e4] hover:text-[#bd9bd1] underline bg-transparent border-none cursor-pointer p-0"
                    >
                      Privacy Policy.
                    </button>
                  </Checkbox>
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item className="mb-0 pt-4">
                  {/* Sign Up Button and Login Link */}
                  <div className="flex items-center justify-center gap-4 flex-wrap">
                    <Button
                      htmlType="submit"
                      size="large"
                      disabled={loading}
                      className=" w-full h-12 bg-[#9161ad] hover:!bg-[#BC9BCF]/90 !text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
                    >
                      {loading ? "Creating Account..." : "SIGN UP"}
                    </Button>
                    <p className="text-white text-base">
                      Already Have account?{" "}
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate("/login")}
                      className="font-semibold text-[#cca1e4] hover:text-[#bd9bd1] transition-colors text-lg"
                    >
                      Login
                    </button>
                  </div>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>
      </div>

      {/* USER LICENSE ACCEPTANCE AGREEMENT Modal */}
      <Modal
        title={
          <span style={{ color: "#cca1e4", fontSize: "20px" }}>
            XynexAI - Global User License Agreement(ULA)
          </span>
        }
        open={userAgreementModalVisible}
        onCancel={() => setUserAgreementModalVisible(false)}
        footer={[
          <Button
            key="decline"
            onClick={() => setUserAgreementModalVisible(false)}
            style={{
              backgroundColor: "#9ca3af",
              color: "white",
              border: "none",
            }}
          >
            Decline
          </Button>,
          <Button
            key="accept"
            type="primary"
            onClick={handleUserAgreementAccept}
            style={{
              backgroundColor: "#cca1e4",
              borderColor: "#4db8b8",
            }}
          >
            Accept
          </Button>,
        ]}
        width={700}
      >
        {<UserAgreementContent />}
      </Modal>

      {/* TERMS & CONDITIONS Modal */}
      <Modal
        title={
          <span style={{ color: "#cca1e4", fontSize: "20px" }}>
            XynexAI – Privacy Policy
          </span>
        }
        open={termsModalVisible}
        onCancel={() => setTermsModalVisible(false)}
        footer={[
          <Button
            key="decline"
            onClick={() => setTermsModalVisible(false)}
            style={{
              backgroundColor: "#9ca3af",
              color: "white",
              border: "none",
            }}
          >
            Decline
          </Button>,
          <Button
            key="accept"
            type="primary"
            onClick={handleTermsAccept}
            style={{
              backgroundColor: "#cca1e4",
              borderColor: "#4db8b8",
            }}
          >
            Accept
          </Button>,
        ]}
        width={700}
      >
        {<PrivacyPolicyContent />}
      </Modal>
    </div>
  );
};

export default SignUp;